import { useState, useCallback, useMemo, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import PaymentMethod from "../api/settings/PaymentMethod";
import Request from "../utils/Request";
import { api_version } from "../settings";
import { responseValidate } from "../utils/functions";

export const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_KEY
);

const usePaymentMethods = (storeId) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);

  const setupIntentPromiseRef = useRef(null);

  const fetchPaymentMethods = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);

    try {
      const response = await PaymentMethod.index({
        store_id: storeId,
      });

      const result = responseValidate(response);

      if (result?.status) {
        setPaymentMethods(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Payment Methods Error:", error);
    } finally {
      setLoading(false);
    }
  }, [storeId]);


  const savedCards = useMemo(() =>
      paymentMethods.filter(
        (method) => method.type === "card"
      ),
    [paymentMethods]
  );


  const savedACH = useMemo(() =>
      paymentMethods.filter(
        (method) =>
          method.type === "us_bank_account"
      ),
    [paymentMethods]
  );


  const createSetupIntent = useCallback(async () => {
    if (clientSecret) return clientSecret;

    if (setupIntentPromiseRef.current) {
      return setupIntentPromiseRef.current;
    }

    setupIntentPromiseRef.current = Request.post(`${api_version}/ach/create-setup-intent`,{store_id: storeId})
      .then((result) => {
        if (result?.data?.status) {
          const secret = result.data.data.client_secret;

          setClientSecret(secret);

          return secret;
        }

        return null;
      })
      .catch((error) => {
        console.error("Create Setup Intent Error:",error);

        return null;
      })
      .finally(() => {
        setupIntentPromiseRef.current = null;
      });

    return setupIntentPromiseRef.current;
  }, [clientSecret, storeId]);

  /**
   * Save payment method
   */
  const savePaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        const response =
          await PaymentMethod.store({
            payment_method_id: paymentMethodId,
            store_id: storeId,
          });

        return responseValidate(response);
      } catch (error) {
        console.error(
          "Save Payment Method Error:",
          error
        );

        return {
          status: false,
          message: "Something went wrong",
        };
      }
    },
    [storeId]
  );

  return {
    loading,
    clientSecret,
    paymentMethods,
    savedCards,
    savedACH,
    fetchPaymentMethods,
    createSetupIntent,
    savePaymentMethod,
  };
};

export default usePaymentMethods;